import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';
import { createChapter, deleteChapter, getChapterById, getChapters, getChaptersBySeriesId, updateChapter } from '../../api/admin/adminChaptersApi';

export const useChapters = (params) => {
  const { setError } = useAuthStore();
  
  const {
    data: chaptersData,
    isLoading,
    error,
    refetch
  } = useQuery(['chapters', params], () => getChapters(params), {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch chapters');
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    chaptersData,
    isLoading,
    error,
    refetchChapters: refetch
  };
};

export const useCreateChapter = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(createChapter, {
    onSuccess: () => {
      toast.success('Chapter created successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('chapters');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create chapter';
      setError(errorMessage);
    },
  });

  return {
    createChapter: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useUpdateChapter = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(
    ({ chapterId, formData }) => updateChapter(chapterId, formData),
    {
      onSuccess: () => {
        toast.success('Chapter updated successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        queryClient.invalidateQueries('chapters');
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to update chapter';
        setError(errorMessage);
        toast.error(errorMessage);
      },
    }
  );

  return {
    updateChapter: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useDeleteChapter = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const mutation = useMutation(deleteChapter, {
    onSuccess: () => {
      toast.success('Chapter deleted successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      // Invalidate both query types
      queryClient.invalidateQueries('chapters');
      queryClient.invalidateQueries('seriesChapters');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete chapter';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    deleteChapter: mutation.mutate,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    error: mutation.error,
  };
};

export const useChapterById = (chapterId) => {
  const { setError } = useAuthStore();
  
  const {
    data: chapterData,
    isLoading,
    error,
    refetch
  } = useQuery(['chapter', chapterId], () => getChapterById(chapterId), {
    onError: (error) => {
      setError(error?.response?.data?.message || `Failed to fetch chapter with ID ${chapterId}`);
    },
    retry: false,
    enabled: !!chapterId, // Only run query if chapterId exists
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    chapterData,
    isLoading,
    error,
    refetchChapter: refetch
  };
};

export const useChaptersBySeriesId = (seriesId) => {
  const { setError } = useAuthStore();
  
  const {
    data: chaptersData,
    isLoading,
    error,
    refetch
  } = useQuery(['seriesChapters', seriesId], () => getChaptersBySeriesId(seriesId), {
    onError: (error) => {
      setError(error?.response?.data?.message || `Failed to fetch chapters for series ID ${seriesId}`);
    },
    retry: false,
    enabled: !!seriesId, // Only run query if seriesId exists
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    chaptersData,
    isLoading,
    error,
    refetchChapters: refetch
  };
};